import { Head, router, usePage } from "@inertiajs/react"
import { useState } from "react"

import { CallForwardingBanner } from "@/components/agents/dashboard/receptionist/call-forwarding-banner"
import { AddJobModal } from "@/components/billing/add-job-modal"
import { ManageDrawer, type ManagedJob } from "@/components/jobs/manage-drawer"
import {
  PageDescription,
  PageHeading,
  PageLayout,
} from "@/components/page-layout"
import { AgentMark, markForSlug } from "@/components/ui/agent-mark"
import { Button } from "@/components/ui/button"
import AppLayout from "@/layouts/app-layout"
import { cn } from "@/lib/utils"
import { agentPath, jobsPath } from "@/routes"

// IMP-520/523: the Jobs page — a card per job. Hired jobs carry their monthly
// stat and a Manage action opening the Manage drawer; open roles carry their
// pitch and an Add action with the price riding in the button (IMP-465's
// purchase modal opens in place).

interface Job {
  key: string
  hired: boolean
  slug: string | null
  name: string
  // Hired jobs
  agent_uid?: string
  pending_setup?: boolean
  setup_started?: boolean
  paused?: boolean
  live?: boolean
  manageable?: boolean
  identifier?: string | null
  stat?: string | null
  // Open roles
  archetype_uid?: string
  pitch?: string | null
  monthly_price_cents?: number | null
}

interface Props {
  jobs: Job[]
  forwarding_reminder: { agent_uid: string; phone_number: string } | null
  manage_job: ManagedJob | null
}

function formatPrice(cents: number): string {
  return `$${Math.round(cents / 100).toLocaleString("en-US")}/mo`
}

function JobCard({
  job,
  onAdd,
  onManage,
}: {
  job: Job
  onAdd: (uid: string) => void
  onManage: (uid: string) => void
}) {
  // Pending setup wins over paused: a job that hasn't been set up can't
  // meaningfully be running, so that's the state worth naming.
  const status = job.pending_setup
    ? "Pending setup"
    : job.paused
      ? "Paused"
      : null

  const action = job.hired ? (
    <Button
      variant="outline"
      size="sm"
      className="h-8 rounded-md px-3.5 shadow-xs"
      onClick={() => {
        if (!job.agent_uid) return
        // IMP-523: launched trio jobs manage in the drawer; drafts continue
        // to their setup guide, and unmanaged jobs to their own page.
        if (job.manageable) onManage(job.agent_uid)
        else router.visit(agentPath({ id: job.agent_uid }))
      }}
    >
      {job.pending_setup
        ? job.setup_started
          ? "Finish setup"
          : "Set up"
        : "Manage"}
    </Button>
  ) : (
    <Button
      size="sm"
      className="h-8 rounded-md px-3.5 shadow-xs"
      onClick={() => onAdd(job.archetype_uid ?? job.key)}
    >
      {job.monthly_price_cents != null
        ? `Add for ${formatPrice(job.monthly_price_cents)}`
        : "Add"}
    </Button>
  )

  return (
    <div className="bg-card rounded-lg border p-4 shadow-xs">
      <div className="flex items-start justify-between gap-3">
        <div className="bg-muted flex size-10 shrink-0 items-center justify-center rounded-lg">
          <AgentMark
            kind={markForSlug(job.slug)}
            className={cn(
              "size-7",
              job.hired ? "text-foreground" : "text-muted-foreground grayscale",
            )}
          />
        </div>
        {action}
      </div>

      <p className="mt-4 text-[15px]/6 font-medium">{job.name}</p>

      {/* One line under the name. A status takes that line rather than
          crowding the name beside it — a job that isn't set up or is paused
          has nothing useful to say in the stat slot anyway. */}
      {status ? (
        <p className="mt-1 text-sm text-amber-700 dark:text-amber-400">
          {status}
        </p>
      ) : (
        <p className="text-muted-foreground mt-1 text-sm">
          {job.hired ? (job.stat ?? job.identifier ?? " ") : job.pitch}
        </p>
      )}
    </div>
  )
}

export default function Jobs({ jobs, forwarding_reminder, manage_job }: Props) {
  // IMP-465: the add-a-job purchase modal, opened from a card — or on
  // arrival when an activation link lands here with ?add=<uid>.
  const [addingUid, setAddingUid] = useState<string | null>(() =>
    new URLSearchParams(window.location.search).get("add"),
  )
  // IMP-523: the Manage drawer, driven by ?manage=<uid> like the feed's
  // conversation drawer. Closing is optimistic while the partial visit
  // clears the prop behind it.
  const [closing, setClosing] = useState(false)
  const [manageView, setManageView] = useState<"manage" | "forwarding">(
    "manage",
  )
  const drawerOpen = manage_job != null && !closing

  // IMP-523: the forwarding nudge survives the dashboard retirement as a
  // dismissible banner. localStorage because forwarding is optional — it
  // should stop nagging, not persist server-side.
  const dismissKey = forwarding_reminder
    ? `jobs-fwd-dismissed:${forwarding_reminder.agent_uid}`
    : null
  const [forwardingDismissed, setForwardingDismissed] = useState(() =>
    dismissKey ? localStorage.getItem(dismissKey) === "1" : true,
  )

  function openManage(uid: string, view: "manage" | "forwarding" = "manage") {
    setClosing(false)
    setManageView(view)
    router.visit(jobsPath({ manage: uid }), {
      only: ["manage_job"],
      preserveState: true,
      preserveScroll: true,
    })
  }

  function closeManage() {
    setClosing(true)
    router.visit(jobsPath(), {
      only: ["manage_job"],
      preserveState: true,
      preserveScroll: true,
      onFinish: () => setClosing(false),
    })
  }

  const partner = usePage().props.partner as
    | {
        name?: string
        billing_type?: string
        stripe_payouts_ready?: boolean
      }
    | undefined
  const imperativeManaged = partner?.billing_type === "imperative_managed"
  const billingBlocked =
    imperativeManaged && partner?.stripe_payouts_ready !== true

  return (
    <AppLayout>
      <Head title="Jobs" />

      {/* Same reading column as Home. */}
      <PageLayout className="max-w-4xl">
        <div className="flex flex-col gap-1">
          <PageHeading>Jobs</PageHeading>
          <PageDescription>The work your assistant takes on.</PageDescription>
        </div>

        {forwarding_reminder && !forwardingDismissed && (
          <CallForwardingBanner
            onSetUp={() =>
              openManage(forwarding_reminder.agent_uid, "forwarding")
            }
            onDismiss={() => {
              if (dismissKey) localStorage.setItem(dismissKey, "1")
              setForwardingDismissed(true)
            }}
          />
        )}

        {jobs.length === 0 ? (
          <div className="text-muted-foreground rounded-xl border border-dashed px-6 py-10 text-center text-sm">
            No jobs yet. Contact your provider to get started.
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {jobs.map((job) => (
              <JobCard
                key={job.key}
                job={job}
                onAdd={setAddingUid}
                onManage={openManage}
              />
            ))}
          </div>
        )}

        <AddJobModal
          archetypeUid={addingUid}
          onClose={() => setAddingUid(null)}
          billingBlocked={billingBlocked}
          partnerName={partner?.name ?? null}
          imperativeManaged={imperativeManaged}
        />
      </PageLayout>

      <ManageDrawer
        job={manage_job}
        open={drawerOpen}
        initialView={manageView}
        onClose={closeManage}
      />
    </AppLayout>
  )
}
